import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Download, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Users,
  Filter,
  FileText
} from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { AttendanceReport, ClassReport, BehaviorReport, TeacherAttendanceReport, SavedFilter } from '../types';
import { format, startOfWeek, endOfWeek, subDays, subMonths } from 'date-fns';
import { reportService } from '../services/localReportService';
import toast from 'react-hot-toast';

const ReportsPage: React.FC = () => {
  const { students, classes, selectedClass, setSelectedClass } = useApp();
  const [reportType, setReportType] = useState<'student' | 'class' | 'behavior' | 'teacher'>('student');
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [reports, setReports] = useState<{
    student?: AttendanceReport;
    class?: ClassReport;
    behavior?: BehaviorReport;
    teacher?: TeacherAttendanceReport;
  }>({});
  const [loading, setLoading] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');

  useEffect(() => {
    loadSavedFilters();
  }, []);

  const loadSavedFilters = async () => {
    // This would load from the database
    setSavedFilters([]);
  };

  const generateReport = async () => {
    if (reportType !== 'teacher' && !selectedClass) {
      toast.error('Please select a class');
      return;
    }

    setLoading(true);
    try {
      if (reportType === 'student' && selectedStudent) {
        const studentReport = await reportService.generateStudentReport(
          selectedStudent,
          dateRange.start,
          dateRange.end
        );
        setReports({ student: studentReport });
      } else if (reportType === 'class') {
        const classReport = await reportService.generateClassReport(
          selectedClass!,
          dateRange.start,
          dateRange.end
        );
        setReports({ class: classReport });
      } else if (reportType === 'behavior') {
        console.log('Generating behavior report for class:', selectedClass, 'date range:', dateRange);
        const behaviorReport = await reportService.generateBehaviorReport(
          selectedClass!,
          dateRange.start,
          dateRange.end
        );
        setReports({ behavior: behaviorReport });
      } else if (reportType === 'teacher') {
        console.log('Generating teacher attendance report for date range:', dateRange);
        const teacherReport = await reportService.generateTeacherAttendanceReport(
          dateRange.start,
          dateRange.end
        );
        console.log('Teacher report generated:', teacherReport);
        setReports({ teacher: teacherReport });
      }
    } catch (error) {
      console.error('Report generation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate report';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const saveFilter = () => {
    if (!newFilterName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }

    const filter: Omit<SavedFilter, 'id' | 'createdAt' | 'updatedAt'> = {
      name: newFilterName,
      type: 'attendance',
      filters: {
        classIds: selectedClass ? [selectedClass] : undefined,
        dateRange: {
          start: dateRange.start,
          end: dateRange.end
        }
      }
    };

    // This would save to the database
    setSavedFilters(prev => [...prev, { ...filter, id: Date.now().toString(), createdAt: new Date(), updatedAt: new Date() }]);
    setNewFilterName('');
    setShowFilterModal(false);
    toast.success('Filter saved successfully');
  };

  const applyFilter = (filter: SavedFilter) => {
    if (filter.filters.classIds?.[0]) {
      setSelectedClass(filter.filters.classIds[0]);
    }
    if (filter.filters.dateRange) {
      setDateRange(filter.filters.dateRange);
    }
  };

  const exportToPDF = () => {
    // This would generate and download a PDF
    toast.success('PDF export started');
  };

  const getQuickDateRanges = () => [
    { label: 'Last 7 days', start: format(subDays(new Date(), 7), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') },
    { label: 'Last 30 days', start: format(subDays(new Date(), 30), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') },
    { label: 'This week', start: format(startOfWeek(new Date()), 'yyyy-MM-dd'), end: format(endOfWeek(new Date()), 'yyyy-MM-dd') },
    { label: 'Last month', start: format(subMonths(new Date(), 1), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }
  ];

  // For now, show all students since we need to implement class-student relationships
  const classStudents = selectedClass ? [] : students;

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Reports</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Generate and analyze attendance and behavior reports</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowFilterModal(true)}
              className="btn-secondary flex items-center"
            >
              <Filter className="h-5 w-5 mr-2" />
              Save Filter
            </button>
            <button
              onClick={exportToPDF}
              className="btn-primary flex items-center"
            >
              <Download className="h-5 w-5 mr-2" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Report Configuration */}
        <div className="card mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Report Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value as any)}
                className="input-field"
              >
                <option value="student">Student Report</option>
                <option value="class">Class Report</option>
                <option value="behavior">Behavior Report</option>
                <option value="teacher">Teacher Attendance Report</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Class
              </label>
              <select
                value={selectedClass || ''}
                onChange={(e) => setSelectedClass(e.target.value || null)}
                className="input-field"
              >
                <option value="">Select a class</option>
                {classes.map(classItem => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </option>
                ))}
              </select>
            </div>

            {reportType === 'student' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Student
                </label>
                <select
                  value={selectedStudent}
                  onChange={(e) => setSelectedStudent(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select a student</option>
                  {classStudents.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.firstName} {student.lastName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-end">
              <button
                onClick={generateReport}
                disabled={loading || (reportType !== 'teacher' && !selectedClass) || (reportType === 'student' && !selectedStudent)}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="input-field"
              />
            </div>
          </div>

          {/* Quick Date Ranges */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quick Ranges
            </label>
            <div className="flex flex-wrap gap-2">
              {getQuickDateRanges().map((range, index) => (
                <button
                  key={index}
                  onClick={() => setDateRange({ start: range.start, end: range.end })}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 rounded-md transition-colors"
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Saved Filters */}
        {savedFilters.length > 0 && (
          <div className="card mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Saved Filters</h3>
            <div className="flex flex-wrap gap-2">
              {savedFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => applyFilter(filter)}
                  className="px-3 py-1 text-sm bg-primary-100 text-primary-800 hover:bg-primary-200 rounded-md transition-colors"
                >
                  {filter.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Student Report */}
        {reports.student && (
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Student Report</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {format(new Date(dateRange.start), 'MMM d')} - {format(new Date(dateRange.end), 'MMM d, yyyy')}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{reports.student.attendanceRate}%</div>
                <div className="text-sm text-blue-800">Attendance Rate</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{reports.student.currentStreak}</div>
                <div className="text-sm text-green-800">Current Streak</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{reports.student.longestStreak}</div>
                <div className="text-sm text-purple-800">Longest Streak</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{reports.student.behaviorIncidents}</div>
                <div className="text-sm text-red-800">Behavior Incidents</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Attendance Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Days</span>
                    <span className="font-medium">{reports.student.totalDays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Present Days</span>
                    <span className="font-medium text-green-600">{reports.student.presentDays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Absent Days</span>
                    <span className="font-medium text-red-600">{reports.student.absentDays}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Flags</h4>
                {reports.student.flags.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No flags</p>
                ) : (
                  <div className="space-y-2">
                    {reports.student.flags.map((flag, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${
                          flag.type === 'red' ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {flag.type.toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-600">{flag.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Class Report */}
        {reports.class && (
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Class Report</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {format(new Date(dateRange.start), 'MMM d')} - {format(new Date(dateRange.end), 'MMM d, yyyy')}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{reports.class.totalStudents}</div>
                <div className="text-sm text-blue-800">Total Students</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{reports.class.averageAttendance.toFixed(1)}%</div>
                <div className="text-sm text-green-800">Avg Attendance</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{reports.class.totalBehaviorIncidents}</div>
                <div className="text-sm text-red-800">Behavior Incidents</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{reports.class.needsAttention.length}</div>
                <div className="text-sm text-yellow-800">Need Attention</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Top Performers</h4>
                {reports.class.topPerformers.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No top performers in this period</p>
                ) : (
                  <div className="space-y-1">
                    {reports.class.topPerformers.map((name, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                        <span className="text-sm">{name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Need Attention</h4>
                {reports.class.needsAttention.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">All students are doing well!</p>
                ) : (
                  <div className="space-y-1">
                    {reports.class.needsAttention.map((name, index) => (
                      <div key={index} className="flex items-center">
                        <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
                        <span className="text-sm">{name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Behavior Report */}
        {reports.behavior && (
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Behavior Report</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {format(new Date(dateRange.start), 'MMM d')} - {format(new Date(dateRange.end), 'MMM d, yyyy')}
                </span>
                <button className="btn-secondary flex items-center">
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{reports.behavior.totalIncidents}</div>
                <div className="text-sm text-red-800">Total Incidents</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{reports.behavior.resolvedIncidents}</div>
                <div className="text-sm text-green-800">Resolved</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{reports.behavior.unresolvedIncidents}</div>
                <div className="text-sm text-yellow-800">Unresolved</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{reports.behavior.studentsWithIncidents.length}</div>
                <div className="text-sm text-blue-800">Students Involved</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Incidents by Type</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Profane Language</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{reports.behavior.incidentsByType['profane-language']}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Disruption</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{reports.behavior.incidentsByType.disruption}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Aggression</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{reports.behavior.incidentsByType.aggression}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Other</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{reports.behavior.incidentsByType.other}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Incidents by Severity</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">High</span>
                    <span className="text-sm font-medium text-red-600">{reports.behavior.incidentsBySeverity.high}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Medium</span>
                    <span className="text-sm font-medium text-yellow-600">{reports.behavior.incidentsBySeverity.medium}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Low</span>
                    <span className="text-sm font-medium text-green-600">{reports.behavior.incidentsBySeverity.low}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Students with Incidents</h4>
              {reports.behavior.studentsWithIncidents.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No behavior incidents in this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Incidents</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Incident</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {reports.behavior.studentsWithIncidents.map((student) => (
                        <tr key={student.studentId}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {student.studentName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {student.incidentCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {format(new Date(student.lastIncidentDate), 'MMM d, yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Teacher Attendance Report */}
        {reports.teacher && (
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Teacher Attendance Report</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {format(new Date(dateRange.start), 'MMM d')} - {format(new Date(dateRange.end), 'MMM d, yyyy')}
                </span>
                <button className="btn-secondary flex items-center">
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">{reports.teacher.totalDays}</div>
                <div className="text-sm text-blue-800 dark:text-blue-200">Total Days</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-300">{reports.teacher.presentDays}</div>
                <div className="text-sm text-green-800 dark:text-green-200">Present Days</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-300">{reports.teacher.absentDays}</div>
                <div className="text-sm text-red-800 dark:text-red-200">Absent Days</div>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-300">{reports.teacher.attendanceRate.toFixed(1)}%</div>
                <div className="text-sm text-purple-800 dark:text-purple-200">Attendance Rate</div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Attendance by Class</h4>
              {reports.teacher.classes.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No teacher attendance records in this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Class</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Days</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Present</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Absent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {reports.teacher.classes.map((cls) => (
                        <tr key={cls.classId}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {cls.className}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {cls.totalDays}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                            {cls.presentDays}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                            {cls.absentDays}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {cls.attendanceRate.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Save Filter</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Filter Name
                    </label>
                    <input
                      type="text"
                      value={newFilterName}
                      onChange={(e) => setNewFilterName(e.target.value)}
                      className="input-field"
                      placeholder="Enter filter name"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveFilter}
                      className="btn-primary"
                    >
                      Save Filter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
